/* eslint @typescript-eslint/no-explicit-any:0 */
import EventEmitter from 'eventemitter3'
import { ulid } from 'ulid'
import { metric } from '@s4l/monitoring'
import { CACHE_METADATA, type CacheMetadata } from '@s4l/cache'
import debug from 'debug'
import { type AllowedConfigKeys, type Base, Config } from './_config'
import type { Model, UseCaseConstructor } from './_kernel/architecture'
import type { ID } from './_kernel/ID'
import type { ListOfID } from './_kernel/ListOfID'

/** Auth */
import type { User } from './auth/domain/User'
import type { UserDeletionStatus } from './auth/domain/UserDeletionStatus'

/** Booking */
import type { CreateGuestRoomingListBookingUseCaseInput } from './booking/application/CreateGuestRoomingListBookingUseCase'
import type { CreateSpecialRequestBookingUseCaseInput } from './booking/application/CreateSpecialRequestBookingUseCase'
import type { CreateInvoiceRequestBookingUseCaseInput } from './booking/application/CreateInvoiceRequestBookingUseCase'
import type { GetListOfInsuranceBookingUseCaseInput } from './booking/domain/InsuranceType'
import type { GetVoucherPaymentStatusBookingUseCaseInput } from './booking/application/GetVoucherPaymentStatusBookingUseCase'
import type { VoucherPaymentConfirmationInput } from './booking/application/GetVoucherPaymentConfirmationBookingUseCase'
import type { RequestStatus } from './booking/domain/RequestStatus'
import type { ResponseMessage } from './booking/domain/ResponseMessage'
import type { ListOfAncillary } from './booking/domain/ListOfAncillary'
import type { ListOfInsurance } from './booking/domain/ListOfInsurance'
import type { SupportInfo } from './booking/domain/SupportInfo'
import type { PaymentStatus } from './booking/domain/PaymentStatus'
import type { VoucherPaymentConfirmation } from './booking/domain/VoucherPaymentConfirmation'

/** Location */
import type { GetLocationInput } from './location/application/GetLocationUseCase'

/** Marketing */
import type { GetTopDestinationsMarketingUseCaseInput } from './marketing/application/GetTopDestinationsMarketingUseCase'
import type { ListOfTopDestination } from './marketing/domain/ListOfTopDestination'
import type { Promotion } from './marketing/domain/Promotion'
import type { SubscribeToNewsletterMarketingUseCaseInput } from './marketing/application/SubscribeToNewsletterMarketingUseCase'
import type { SubscriptionStatus } from './marketing/domain/SubscriptionStatus'

/** Property */
import type { ListOfProperty } from './property/domain/ListOfProperty'
import type { RemoveByIDLastSeenPropertyUseCaseInput } from './property/application/RemoveByIDLastSeenPropertyUseCase'
import type { AddByIDLastSeenPropertyUseCaseInput } from './property/application/AddByIDLastSeenPropertyUseCase'
import type { GetRelatedPropertyUseCaseInput } from './property/application/GetRelatedPropertyUseCase'
import type { LikeUserPropertyUseCaseInput } from './property/application/LikeUserPropertyUseCase'
import type { UnlikeUserPropertyUseCaseInput } from './property/application/UnlikeUserPropertyUseCase'
import type { GetUserLikesPropertyUseCaseInput } from './property/application/GetUserLikesPropertyUseCase'
import type { LikeBelong } from './property/domain/LikeBelong'
import type { ListOfPaginatedProperty } from './property/domain/ListOfPaginatedProperty'
import type { GetListOfLikesPropertyUseCaseInput } from './property/application/GetListOfLikesPropertyUseCase'
import type { GetListOfBannerPropertyUseCaseInput } from './property/application/GetListOfBannerPropertyUseCase'
import type { GetListOfAvailabilityBasicBookingUseCaseInput } from './booking/application/GetListOfAvailabilityBasicBookingUseCase'
import type { ListOfAvailabilityBasic } from './booking/domain/ListOfAvailabilityBasic'
import type { ListOfBanner } from './marketing/domain/ListOfBanner'
import type { LoginStatus } from './auth/domain/LoginStatus'
import type { ListOfTheme } from './property/domain/ListOfTheme'
import type { Search } from './property/domain/Search'
import type { SearchCriteria } from './property/domain/SearchCriteria'
import type { SearchResult } from './property/domain/SearchResult'
import type { SearchFromPartialAndCriteriaPropertyUseCaseInput } from './property/application/SearchFromPartialAndCriteriaPropertyUseCase'

/** Shared */
import type { Affiliate } from './shared/domain/Affiliate'
import type { Language } from './shared/domain/Language'
import type { Market } from './shared/domain/Market'
import type { Source } from './shared/domain/Source'
import type { GetFeatureFlagByNameSharedUseCaseInput } from './shared/application/GetFeatureFlagByNameSharedUseCase'
import type { FeatureFlag } from './shared/domain/FeatureFlag'
import type { Platform } from './shared/domain/Platform'
import type { OperatingSystem } from './shared/domain/OperatingSystem'
import type { PriceType } from './_kernel/PriceType'
import type { Currency } from './_kernel/Currency'
import type { Location } from './shared/domain/Location'
import type { MobileRate } from './shared/domain/MobileRate'

const log = debug('s4l:domain:entrypoint')

// Export fetcher types
export type { FetcherRequestEvent, FetcherRequestEventListener } from './_fetcher/Fetcher'

// Extract UseCase getter names dynamically
export type GetterKeys<T> = {
  [K in keyof T]: T[K] extends { execute: (...args: any[]) => any } ? K : never
}[keyof T] &
  string

export type UseCaseGetterNames = GetterKeys<StayForLong>

export type UseCaseEvent<T extends UseCaseGetterNames = UseCaseGetterNames> = {
  usecase: T
  args: Parameters<StayForLong[T]['execute']>[0]
  response: Awaited<ReturnType<StayForLong[T]['execute']>>
}

export interface StayForLong {
  on<T extends UseCaseGetterNames>(event: T, listener: (data: UseCaseEvent<T>) => void): this
  on(event: '*', listener: (data: UseCaseEvent) => void): this

  off<T extends UseCaseGetterNames>(event: T, listener: (data: UseCaseEvent<T>) => void): this
  off(event: '*', listener: (data: UseCaseEvent) => void): this
}

export class StayForLong extends EventEmitter {
  private __config: Config

  static create(config: Base) {
    log('create with config base', config)
    return new StayForLong(config)
  }

  static empty() {
    return new StayForLong({
      ENVIRONMENT: 'development',
      COOKIES_STRING: '',
      API_GATEWAY: 'NO_API_GATEWAY_IN_EMPTY_DOMAIN',
      IMAGE_SERVICE: 'NO_IMAGE_SERVICE_IN_EMPTY_DOMAIN',
      STATICS_HOST: 'NO_STATICS_HOST_IN_EMPTY_DOMAIN',
      BANNERS_FILE_NAME: 'NO_BANNERS_FILE_NAME_IN_EMPTY_DOMAIN',
    })
  }

  config(): Config
  config(key?: AllowedConfigKeys) {
    return key ? this.__config.get(key) : this.__config
  }

  constructor(config: Base) {
    super()
    this.__config = Config.create(config)
  }

  /* Auth */
  get GetCurrentUserAuthUseCase(){ return this.getter<undefined, User>( () => import('./auth/application/GetCurrentUserAuthUseCase'))} // prettier-ignore
  get GetCurrentUserIdAuthUseCase(){ return this.getter<undefined, ID | null>( () => import('./auth/application/GetCurrentUserIdAuthUseCase'))} // prettier-ignore
  get GetCurrentLoginStatusAuthUseCase(){ return this.getter<undefined, LoginStatus>( () => import('./auth/application/GetCurrentLoginStatusAuthUseCase'))} // prettier-ignore
  get DeleteCurrentUserAuthUseCase(){ return this.getter<undefined, UserDeletionStatus>( () => import('./auth/application/DeleteCurrentUserAuthUseCase'))} // prettier-ignore
  get GetCurrentUserDeletionStatusAuthUseCase(){ return this.getter<undefined, UserDeletionStatus>( () => import('./auth/application/GetCurrentUserDeletionStatusAuthUseCase'))} // prettier-ignore

  /* Booking */
  get CreateGuestRoomingListBookingUseCase() { return this.getter<CreateGuestRoomingListBookingUseCaseInput, ResponseMessage>(() => import('./booking/application/CreateGuestRoomingListBookingUseCase')) } // prettier-ignore
  get CreateInvoiceRequestBookingUseCase(){ return this.getter<CreateInvoiceRequestBookingUseCaseInput, ResponseMessage>( () => import('./booking/application/CreateInvoiceRequestBookingUseCase'))} // prettier-ignore
  get CreateSpecialRequestBookingUseCase(){ return this.getter<CreateSpecialRequestBookingUseCaseInput, RequestStatus>( () => import('./booking/application/CreateSpecialRequestBookingUseCase'))} // prettier-ignore
  get GetAncillariesBookingUseCase() { return this.getter<undefined, ListOfAncillary>(() => import('./booking/application/GetAncillariesBookingUseCase')) } // prettier-ignore
  get GetListOfAvailabilityBasicBookingUseCase() { return this.getter<GetListOfAvailabilityBasicBookingUseCaseInput, ListOfAvailabilityBasic>(() => import('./booking/application/GetListOfAvailabilityBasicBookingUseCase')) } // prettier-ignore
  get GetListOfInsuranceBookingUseCase() { return this.getter<GetListOfInsuranceBookingUseCaseInput, ListOfInsurance>(() => import('./booking/application/GetListOfInsuranceBookingUseCase')) } // prettier-ignore
  get GetSupportInfoBookingUseCase() { return this.getter<undefined, SupportInfo>(() => import('./booking/application/GetSupportInfoBookingUseCase')) } // prettier-ignore
  get GetVoucherPaymentConfirmationBookingUseCase() { return this.getter<VoucherPaymentConfirmationInput, VoucherPaymentConfirmation>(() => import('./booking/application/GetVoucherPaymentConfirmationBookingUseCase')) } // prettier-ignore
  get GetVoucherPaymentStatusBookingUseCase() { return this.getter<GetVoucherPaymentStatusBookingUseCaseInput, PaymentStatus | null>(() => import('./booking/application/GetVoucherPaymentStatusBookingUseCase')) } // prettier-ignore

  /* Location */
  get GetLocationUseCase() { return this.getter<GetLocationInput, Location>(() => import('./location/application/GetLocationUseCase')) } // prettier-ignore

  /* Marketing */
  get GetTopDestinationsMarketingUseCase() { return this.getter<GetTopDestinationsMarketingUseCaseInput, ListOfTopDestination>(() => import('./marketing/application/GetTopDestinationsMarketingUseCase')) } // prettier-ignore
  get GetCurrentPromoMarketingUseCase(){ return this.getter<undefined, Promotion>( () => import('./marketing/application/GetCurrentPromoMarketingUseCase'))} // prettier-ignore
  get GetBannersMarketingUseCase(){ return this.getter<undefined, ListOfBanner>( () => import('./marketing/application/GetBannersMarketingUseCase'))} // prettier-ignore
  get SubscribeToNewsletterMarketingUseCase() { return this.getter<SubscribeToNewsletterMarketingUseCaseInput, SubscriptionStatus>(() => import('./marketing/application/SubscribeToNewsletterMarketingUseCase')) } // prettier-ignore

  /* Property */
  get GetLastSeenPropertyUseCase() { return this.getter<undefined, ListOfProperty>(() => import('./property/application/GetLastSeenPropertyUseCase')) } // prettier-ignore
  get AddByIDLastSeenPropertyUseCase(){ return this.getter<AddByIDLastSeenPropertyUseCaseInput, ListOfID>( () => import('./property/application/AddByIDLastSeenPropertyUseCase'))} // prettier-ignore
  get RemoveByIDLastSeenPropertyUseCase(){ return this.getter<RemoveByIDLastSeenPropertyUseCaseInput, ListOfID>( () => import('./property/application/RemoveByIDLastSeenPropertyUseCase'))} // prettier-ignore
  get GetRelatedPropertyUseCase(){ return this.getter<GetRelatedPropertyUseCaseInput, ListOfProperty>( () => import('./property/application/GetRelatedPropertyUseCase'))} // prettier-ignore
  get LikeUserPropertyUseCase(){ return this.getter<LikeUserPropertyUseCaseInput, ID>( () => import('./property/application/LikeUserPropertyUseCase'))} // prettier-ignore
  get UnlikeUserPropertyUseCase(){ return this.getter<UnlikeUserPropertyUseCaseInput, ID>( () => import('./property/application/UnlikeUserPropertyUseCase'))} // prettier-ignore
  get GetUserLikesPropertyUseCase(){ return this.getter<GetUserLikesPropertyUseCaseInput, LikeBelong>( () => import('./property/application/GetUserLikesPropertyUseCase'))} // prettier-ignore
  get GetListOfLikesPropertyUseCase(){ return this.getter<GetListOfLikesPropertyUseCaseInput, ListOfPaginatedProperty>( () => import('./property/application/GetListOfLikesPropertyUseCase'))} // prettier-ignore
  get GetListOfBannerPropertyUseCase(){ return this.getter<GetListOfBannerPropertyUseCaseInput, ListOfProperty>( () => import('./property/application/GetListOfBannerPropertyUseCase'))} // prettier-ignore
  get GetListOfThemesPropertyUseCase(){ return this.getter<undefined, ListOfTheme>( () => import('./property/application/GetListOfThemesPropertyUseCase'))} // prettier-ignore
  get CreateSearchCriteriaPropertyUseCase(){ return this.getter<undefined, SearchCriteria>( () => import('./property/application/CreateSearchCriteriaPropertyUseCase'))} // prettier-ignore
  get CreateSearchPropertyUseCase(){ return this.getter<undefined, Search>(() => import('./property/application/CreateSearchPropertyUseCase'))} // prettier-ignore
  get SearchFromPartialAndCriteriaPropertyUseCase(){ return this.getter<SearchFromPartialAndCriteriaPropertyUseCaseInput, SearchResult>( () => import('./property/application/SearchFromPartialAndCriteriaPropertyUseCase'))} // prettier-ignore

  /* Shared */
  get GetCurrentAffiliateSharedUseCase(){ return this.getter<undefined, Affiliate>( () => import('./shared/application/GetCurrentAffiliateSharedUseCase'))} // prettier-ignore
  get GetCurrentLanguageSharedUseCase(){ return this.getter<undefined, Language>( () => import('./shared/application/GetCurrentLanguageSharedUseCase'))} // prettier-ignore
  get GetCurrentMarketSharedUseCase(){ return this.getter<undefined, Market>( () => import('./shared/application/GetCurrentMarketSharedUseCase'))} // prettier-ignore
  get GetCurrentOperatingSystemSharedUseCase(){ return this.getter<undefined, OperatingSystem>( () => import('./shared/application/GetCurrentOperatingSystemSharedUseCase'))} // prettier-ignore
  get GetCurrentPlatformSharedUseCase(){ return this.getter<undefined, Platform>( () => import('./shared/application/GetCurrentPlatformSharedUseCase'))} // prettier-ignore
  get GetCurrentSourceSharedUseCase(){ return this.getter<undefined, Source>( () => import('./shared/application/GetCurrentSourceSharedUseCase'))} // prettier-ignore
  get GetFeatureFlagByNameSharedUseCase(){ return this.getter<GetFeatureFlagByNameSharedUseCaseInput, FeatureFlag>( () => import('./shared/application/GetFeatureFlagByNameSharedUseCase'))} // prettier-ignore
  get GetPriceTypeSharedUseCase(){ return this.getter<undefined, PriceType>( () => import('./shared/application/GetPriceTypeSharedUseCase'))} // prettier-ignore
  get GetCurrentCurrencySharedUseCase(){ return this.getter<undefined, Currency>( () => import('./shared/application/GetCurrentCurrencySharedUseCase'))} // prettier-ignore
  get GetCurrentMobileRateSharedUseCase(){ return this.getter<undefined, MobileRate>( () => import('./shared/application/GetCurrentMobileRateSharedUseCase'))} // prettier-ignore

  on<T extends UseCaseGetterNames | '*'>(
    event: T,
    listener: (
      data: T extends '*' ? UseCaseEvent : UseCaseEvent<Extract<T, UseCaseGetterNames>>
    ) => void
  ): this {
    return super.on(event as string | symbol, listener as any)
  }

  off<T extends UseCaseGetterNames | '*'>(
    event: T,
    listener: (
      data: T extends '*' ? UseCaseEvent : UseCaseEvent<Extract<T, UseCaseGetterNames>>
    ) => void
  ): this {
    return super.off(event as string | symbol, listener as any)
  }

  private getter<I, O extends Model | null>(
    loader: () => Promise<{ default: UseCaseConstructor<I, O> }>
  ) {
    return {
      execute: async (input?: I): Promise<[undefined, O] | [Error, undefined]> => {
        const Klass = await loader().then(mod => mod.default)
        const uc = Klass.create({ config: this.__config })
        const startTime = Date.now()

        try {
          // @ts-expect-error Input could extends from undefined
          const response = (await uc.execute(input)) as O & {
            [CACHE_METADATA]?: CacheMetadata
          }
          const duration = Date.now() - startTime
          const metadata = response?.[CACHE_METADATA] ?? { cached: false, timestamp: 0 }

          // Send metric asynchronously without blocking the response time
          metric
            .send({
              id: ulid(),
              usecase: Klass.USE_CASE_NAME,
              timestamp: Date.now(),
              duration,
              success: true,
              metadata: { cached: metadata.cached },
            })
            .catch(() => {
              // Silent fail
            })

          const eventData = {
            usecase: Klass.USE_CASE_NAME,
            args: input as I,
            response: [undefined, response] as [undefined, O] | [Error, undefined],
          }
          this.emit(Klass.USE_CASE_NAME, eventData)
          this.emit('*', eventData)
          return [undefined, response]
        } catch (error) {
          const duration = Date.now() - startTime

          // Send metric asynchronously without blocking the response time
          metric
            .send({
              id: ulid(),
              usecase: Klass.USE_CASE_NAME,
              timestamp: Date.now(),
              duration,
              success: false,
              metadata: { cached: false },
            })
            .catch(() => {
              // Silent fail
            })

          const eventData = {
            usecase: Klass.USE_CASE_NAME,
            args: input as I,
            response: [error as Error, undefined] as [undefined, O] | [Error, undefined],
          }
          this.emit(Klass.USE_CASE_NAME, eventData)
          this.emit('*', eventData)
          return [error as Error, undefined]
        }
      },
    }
  }
}
